import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { uploadArtwork } from "../lib/api";
import { UploadCloud, FileCheck2, X, FileText, Image as ImageIcon, FileType } from "lucide-react";

const EXTS = ["pdf", "png", "jpg", "jpeg", "svg", "ai", "eps"];
const PREVIEWABLE_IMG = ["png", "jpg", "jpeg", "svg"];

export function ArtworkUpload({ file, setFile, testPrefix = "sign" }) {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const handleFiles = async (f) => {
    if (!f) return;
    const ext = f.name.split(".").pop()?.toLowerCase();
    if (!EXTS.includes(ext)) { toast.error("Please upload PDF, PNG, JPG, SVG, AI or EPS"); return; }
    if (f.size > 50 * 1024 * 1024) { toast.error("Max 50 MB"); return; }
    const previewUrl = (PREVIEWABLE_IMG.includes(ext) || ext === "pdf") ? URL.createObjectURL(f) : null;
    const previewKind = PREVIEWABLE_IMG.includes(ext) ? "image" : ext === "pdf" ? "pdf" : "other";
    setUploading(true);
    try {
      const res = await uploadArtwork(f);
      setFile({ ...res, name: f.name, previewUrl, previewKind });
      toast.success("Artwork uploaded");
    } catch (e) {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const removeFile = () => {
    if (file?.previewUrl) URL.revokeObjectURL(file.previewUrl);
    setFile(null);
  };
  useEffect(() => () => { if (file?.previewUrl) URL.revokeObjectURL(file.previewUrl); }, [file]);

  return (
    <div
      onClick={() => inputRef.current?.click()}
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files?.[0]); }}
      className={`border-dashed border-2 rounded-2xl p-6 cursor-pointer transition ${dragOver ? "border-[color:var(--brand)] bg-[color:var(--brand-soft)]" : "border-[#E2DDD5]"}`}
      data-testid={`${testPrefix}-upload-dropzone`}
    >
      <input ref={inputRef} type="file" className="hidden" accept=".pdf,.png,.jpg,.jpeg,.svg,.ai,.eps" onChange={(e) => handleFiles(e.target.files?.[0])} data-testid={`${testPrefix}-upload-input`}/>
      {!file && (
        <div className="flex flex-col items-center text-center py-4">
          <UploadCloud size={30} style={{ color: "var(--brand)" }} className="mb-3" />
          <div className="font-display font-bold text-lg">Drag & drop artwork</div>
          <div className="text-xs text-slate-500 mt-1">PDF, PNG, JPG, SVG, AI, EPS · Up to 50 MB</div>
          <button className="btn-primary mt-4 !py-2 !px-4 text-sm">{uploading ? "Uploading…" : "Choose file"}</button>
        </div>
      )}
      {file && (
        <div data-testid={`${testPrefix}-upload-success`} onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center gap-3 mb-4">
            <FileCheck2 size={28} className="text-[#10B981] flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-semibold truncate">{file.name}</div>
              <div className="text-xs text-slate-500">{Math.round(file.size/1024)} KB · uploaded · <span className="font-semibold text-[#10B981]">Ready to go</span></div>
            </div>
            <button onClick={removeFile} className="btn-ghost" aria-label="Remove file" data-testid={`${testPrefix}-upload-remove`}><X size={18}/></button>
          </div>
          <div className="rounded-2xl overflow-hidden border border-[#E2DDD5] bg-[#F3EFEA] relative" data-testid={`${testPrefix}-artwork-preview`}>
            {file.previewKind === "image" && (
              <div className="aspect-[4/3] flex items-center justify-center p-6 bg-[repeating-conic-gradient(#FFFFFF_0%_25%,#F3EFEA_0%_50%)] bg-[length:28px_28px]">
                <img src={file.previewUrl} alt="Your artwork preview" className="max-w-full max-h-full object-contain drop-shadow-[0_6px_20px_rgba(0,0,0,0.08)]" />
              </div>
            )}
            {file.previewKind === "pdf" && (
              <div className="aspect-[4/3] bg-white">
                <object data={`${file.previewUrl}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`} type="application/pdf" className="w-full h-full">
                  <div className="w-full h-full flex items-center justify-center flex-col gap-2 p-6 text-center text-slate-500 text-sm">
                    <FileText size={32} />
                    PDF uploaded — preview not available in this browser.
                  </div>
                </object>
              </div>
            )}
            {file.previewKind === "other" && (
              <div className="aspect-[4/3] flex flex-col items-center justify-center gap-3 p-8 text-center">
                <div className="h-14 w-14 rounded-xl bg-[#003DA5] text-white font-black flex items-center justify-center font-display tracking-tight uppercase">
                  .{file.ext}
                </div>
                <div className="text-sm font-semibold text-slate-700">Vector file uploaded</div>
                <div className="text-xs text-slate-500 max-w-xs">We can't preview {file.ext?.toUpperCase()} files in the browser — our studio will open your file and send you a proof before printing.</div>
              </div>
            )}
            <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/80 backdrop-blur text-white text-[10px] font-bold uppercase tracking-widest">
              {file.previewKind === "image" && <><ImageIcon size={11}/> Preview</>}
              {file.previewKind === "pdf" && <><FileText size={11}/> PDF Preview</>}
              {file.previewKind === "other" && <><FileType size={11}/> {file.ext?.toUpperCase()}</>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
